import {
  Avatar,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { Fragment, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { map } from 'lodash';

import { Button, SearchField } from '@project/components';
import { useDownloadFile } from '@project/hooks/global';
import { operationsService } from '@project/services';
import { useAdminFinesData, useAdminInvoiceData, useAdminUsersData } from '@project/react-queries';

const AdminScreen: React.FC = () => {
  const [searchFines, setSearchFines] = useState<string>('');
  const [searchUserFullName, setSearchUserFullName] = useState<string>('');
  const [searchInvoices, setSearchInvoices] = useState<string>('');
  const [bulkActionsAnchor, setBulkActionsAnchor] = useState<null | HTMLElement>(null);
  const history = useHistory();
  const usersData = useAdminUsersData(1, 12, searchUserFullName, '', '', '', '', '', '');
  const invoiceData = useAdminInvoiceData({
    referenceNumber: searchInvoices,
    offenderIdentifierValue: '',
    recipientName: '',
    vatNumber: '',
    address: '',
    companyRegistrationNumber: '',
    pageNum: 1,
    pageEntries: 12,
  });
  const filterOptions = useMemo(() => {
    return {
      vehicleRegistration: '',
      byNag: false,
      offenderIdentifierValue: '',
      noticeNumber: searchFines,
      pageNum: 1,
      pageEntries: 12,
    };
  }, [searchFines]);

  const fineData = useAdminFinesData(filterOptions);

  const _goToIndividualUser = (id: string) => (): void => {
    history.push(`/admin/individual/user?Id=${id}`);
  };
  const _goToIndividualFine = (id: string) => (): void => {
    history.push(`/admin/individual/fine?Id=${id}`);
  };
  const _goToIndividualInvoice = (id: string) => (): void => {
    history.push(`/admin/individual/invoice/?Id=${id}`);
  };
  const _goFineTable = () => {
    history.push('/admin/fines');
  };
  const _goToBulkTmtCapeTownUpload = () => {
    history.push('/admin/tmt-fines-bulk-upload');
  };
  const _goToTmtFinesUpload = () => {
    history.push('/admin/tmt-fines-upload');
  };
  const _goToBulkFinesUpload = () => {
    history.push('/admin/general-fines-bulk-upload');
  };
  const _goToPayFastEmailUpload = () => {
    history.push('/admin/payfast-email-upload');
  };
  const _goToInvalidEmailUpload = () => {
    history.push('/admin/invalid-email-upload');
  };
  const _goToWhatsAppWhitelistBulkUpload = () => {
    history.push('/admin/whatsapp-whitelist-bulk-upload');
  };
  const _goToDigitalCertificatesBulkGenerate = () => {
    history.push('/admin/digital-certificates-bulk-generate');
  };
  const _goToInvoiceTable = () => {
    history.push('/admin/invoice');
  };
  const _goToUserTable = () => {
    history.push('/admin/users');
  };

  const usersCards = () => {
    if (usersData.isLoading) {
      return <CircularProgress />;
    }
    return (
      <div className=" flex flex-col m-2">
        <Card className="flex h-formThird w-cardThird shadow-2xl">
          <CardContent className="flex flex-col flex-grow">
            <div className="mb-2 font-bold">
              <p>Accounts</p>
            </div>
            <Divider />
            <div className="mb-1">
              <SearchField
                placeholder="Search"
                value={searchUserFullName}
                isLoading={usersData.isLoading}
                onChange={(value) => {
                  setSearchUserFullName(value);
                }}
              />
            </div>
            <Divider />
            <div className="overflow-auto ...">
              {!usersData.isLoading &&
                map(usersData.data.items, (userData) => (
                  <div key={userData.email} className="flex justify-between mb-10">
                    <div className="flex flex-row justify-center justify-items-center">
                      <Avatar className="mr-3 mt-1">
                        {userData?.firstName?.[0] || ''}
                        {userData?.lastName?.[0] || ''}
                      </Avatar>
                      <p className="flex pt-3">
                        {userData?.firstName || ''}
                        &nbsp;{userData?.lastName || ''}
                      </p>
                    </div>
                    <Button
                      onClick={_goToIndividualUser(userData.id)}
                      className="rounded-full mt-2"
                    >
                      View
                    </Button>
                  </div>
                ))}
            </div>
            <div className="flex mt-auto justify-center">
              <Button onClick={_goToUserTable} size="small" className="rounded-full">
                Load More
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const innerInvoicesCards = () => {
    if (invoiceData.isLoading) {
      return <CircularProgress />;
    }

    if (!invoiceData?.data?.items || invoiceData.data.items.length === 0) {
      return <div className="text-center text-gray-500 mt-4">No invoices found</div>;
    }

    return map(invoiceData?.data?.items, (useInvoiceData) => (
      <div className="mb-3">
        <Card className="text-xs shadow-md">
          <CardContent>
            <div className="flex flex-row justify-between">
              <div className="flex flex-col">
                <p className="font-bold mb-2">{useInvoiceData.recipientName}</p>
                <div className="text-gray-500">
                  <p>{useInvoiceData.offenderIdentifier}</p>
                  <p>Fines paid: {useInvoiceData.finesPaid}</p>
                </div>
              </div>
              <div className="ml-10">
                <p>Paid Amount</p>
                <p>R {useInvoiceData.amountPaid}</p>
                <Button
                  onClick={_goToIndividualInvoice(useInvoiceData?.id)}
                  size="small"
                  className="mt-1 bg-secondary bg-opacity-50 text-secondary hover:bg-secondary hover:bg-opacity-50"
                >
                  Full Info
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ));
  };

  const invoicesCard = () => {
    return (
      <div className="flex m-2">
        <Card className=" flex w-cardThird h-formThird shadow-2xl">
          <CardContent className="flex flex-col flex-grow">
            <div className="mb-2 font-bold">
              <p>Invoices</p>
            </div>
            <Divider />
            <div className="mb-1">
              <SearchField
                placeholder="Search"
                value={searchInvoices}
                isLoading={invoiceData.isLoading}
                onChange={(value) => {
                  setSearchInvoices(value);
                }}
              />
            </div>
            <Divider />
            <div className="overflow-auto ...">{innerInvoicesCards()}</div>
            <div className="flex mt-auto justify-center">
              <Button onClick={_goToInvoiceTable} size="small" className="rounded-full">
                Load More
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const innerFinesCards = () => {
    if (fineData.isLoading) {
      return <CircularProgress />;
    }

    if (!fineData?.data?.items || fineData.data.items.length === 0) {
      return <div className="text-center text-gray-500 mt-4">No fines found</div>;
    }

    return map(fineData?.data?.items, (useFineData) => (
      <div className="mb-3">
        <Card className="text-xs shadow-md">
          <CardContent>
            <div className="flex flex-row justify-between">
              <div className="flex flex-col">
                <p className="font-bold mb-2">{useFineData.offenderIdentifier}</p>
                <p className="font-bold">{useFineData.noticeNumber}</p>
                <p>Reg No: {useFineData.vehicleRegistration}</p>
                <div className="flex flex-row justify-between">
                  <p className="">Offense Date: {useFineData.date}</p>
                  <p className="ml-8">Area: {useFineData.area}</p>
                </div>
              </div>
              <div className="border-l-2 ">
                <div className="ml-6">
                  <p>Fine Amount</p>
                  <p>R{useFineData?.amountDue}</p>
                  {useFineData.amountPaid !== useFineData.amountDue ||
                  useFineData.amountDue === 0 ? (
                    <Button
                      onClick={_goToIndividualFine(useFineData?.id)}
                      size="small"
                      className="mt-1 text-xs bg-tertiary bg-opacity-20 text-tertiary hover:bg-tertiary hover:bg-opacity-20"
                    >
                      Full Info
                    </Button>
                  ) : (
                    <Button
                      onClick={_goToIndividualFine(useFineData?.id)}
                      size="small"
                      className="mt-1 bg-total-green bg-opacity-20 text-total-green text-xs hover:bg-total-green hover:bg-opacity-20"
                    >
                      Full Info
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ));
  };

  const finesCard = () => {
    return (
      <Card className="flex w-cardThird h-formThird m-2 shadow-2xl">
        <CardContent className="flex flex-col flex-grow ">
          <div className="mb-2 font-bold">
            <p>New Fines</p>
          </div>
          <Divider />
          <div className="mb-1">
            <SearchField
              placeholder="Search"
              value={searchFines}
              isLoading={fineData.isLoading}
              onChange={(value) => {
                setSearchFines(value);
              }}
            />
          </div>
          <Divider />

          <div className="overflow-auto ...">{innerFinesCards()}</div>
          <div className="flex justify-center mt-auto">
            <Button onClick={_goFineTable} size="small" className="rounded-full ">
              Load More
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const { download, loading: isDownloadingCoctExport } = useDownloadFile({
    apiDefinition: operationsService.exportCapeTownWeeklyOffenderIdentifiers,
    onError: () => toast.error('Could not download COCT export'),
    fileName: 'COCT_Export.csv',
  });

  const bulkActionGroups = [
    {
      heading: 'Uploads',
      actions: [
        { label: 'PayFast Email List', onSelect: _goToPayFastEmailUpload },
        { label: 'Invalid Email List', onSelect: _goToInvalidEmailUpload },
        { label: 'WhatsApp Whitelist', onSelect: _goToWhatsAppWhitelistBulkUpload },
        { label: 'General Fines', onSelect: _goToBulkFinesUpload },
        { label: 'TMT Cape Town', onSelect: _goToBulkTmtCapeTownUpload },
        { label: 'TMT Fines Upload', onSelect: _goToTmtFinesUpload },
      ],
    },
    {
      heading: 'Exports',
      actions: [
        { label: 'TMT Cape Town Export', onSelect: download },
        { label: 'Bulk Generate Certificates', onSelect: _goToDigitalCertificatesBulkGenerate },
      ],
    },
  ];

  const _onBulkActionSelect = (onSelect: () => void) => () => {
    setBulkActionsAnchor(null);
    onSelect();
  };

  return (
    <>
      <div className="border-b border-black mt-8 flex justify-end mr-16 ml-16">
        <Button
          className="text-white bg-opacity-80 bg-secondary mb-2 hover:bg-blue-900 whitespace-nowrap"
          type="text"
          onClick={(event) => setBulkActionsAnchor(event.currentTarget as HTMLElement)}
        >
          <span className="flex flex-row items-center">
            Bulk Actions
            {isDownloadingCoctExport ? (
              <CircularProgress size={14} className="ml-2 text-white" />
            ) : (
              <ExpandMoreIcon fontSize="small" className="ml-1" />
            )}
          </span>
        </Button>
        <Menu
          anchorEl={bulkActionsAnchor}
          open={!!bulkActionsAnchor}
          onClose={() => setBulkActionsAnchor(null)}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {map(bulkActionGroups, (group) => (
            <Fragment key={group.heading}>
              <ListSubheader className="text-xs font-bold leading-8 bg-white">
                {group.heading}
              </ListSubheader>
              {map(group.actions, (action) => (
                <MenuItem
                  key={action.label}
                  onClick={_onBulkActionSelect(action.onSelect)}
                  className="text-sm"
                >
                  {action.label}
                </MenuItem>
              ))}
            </Fragment>
          ))}
        </Menu>
      </div>
      <div className="flex flex-row justify-evenly mt-10">
        {finesCard()}
        {invoicesCard()}
        {usersCards()}
      </div>
    </>
  );
};

export default AdminScreen;
